import React, { Suspense, useEffect, createRef, memo } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  IconButton,
  Typography,
  DrawerHeader,
  DrawerBody
} from '@one-thd/sui-atomic-components';
import { ArrowBack, Close } from '@one-thd/sui-icons';
import { useDrawerContext } from './DrawerContext';
import { AnchoredDrawer } from '../generic-ui/AnchoredDrawer';
import { useScrollLock } from '../../hooks/useScrollLock';
import { HeaderIconTextButton } from '../generic-ui/HeaderIconTextButton';

const HeaderDrawer = () => {
  const { drawerState, drawerDispatch } = useDrawerContext();

  const { lockScroll, unlockScroll } = useScrollLock();

  const anchoredRef = createRef();

  const {
    title,
    drawerContent,
    position,
    navigationTracker
  } = drawerState;

  const open = (drawerContent !== null);

  const canNavigateBack = (navigationTracker?.length > 1) || false;

  useEffect(() => {
    if (open) {
      lockScroll();
    }
    return () => {
      // unlock scroll when drawer is not open
      unlockScroll();
    };
  }, [open]);

  // scroll to top when navigating through menus
  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    anchoredRef?.current?.scrollTo({ top: 0 });
  }, [navigationTracker]);

  const handleDrawerClose = () => {
    drawerDispatch({ type: 'clear' });
  };

  const handleDrawerBack = () => {
    drawerDispatch({ type: 'backtrack' });
  };

  if (!open) {
    return null;
  }

  const DrawerHeaderTitle = () => (
    <Typography data-testid="header-drawer-title" variant="body-base" weight="bold" height="normal">
      <Suspense fallback={<div />}>
        { title }
      </Suspense>
    </Typography>
  );

  const StackedDrawerHeader = () => (
    <DrawerHeader
      startAdornment={(
        <HeaderIconTextButton
          label="Back"
          icon={ArrowBack}
          iconSize="small"
          onClick={handleDrawerBack}
          flexDirection="row"
          aria-label="Click to go back and render previous drawer menus"
        />

      )}
      endAdornment={(
        <IconButton
          aria-label="drawer-close"
          onClick={handleDrawerClose}
          icon={Close}
        />
      )}
    >
      <div className="sui-flex sui-items-center sui-ml-1">
        <DrawerHeaderTitle />
      </div>
    </DrawerHeader>
  );

  return (
    <AnchoredDrawer
      position={position}
      onClickBackdrop={handleDrawerClose}
      ref={anchoredRef}
      data-component="HeaderDrawer"
    >
      <div className="sui-sticky sui-z-max sui-top-0 sui-border-b-1 sui-border-primary sui-bg-primary sui-px-2">
        {(canNavigateBack) ? (
          <StackedDrawerHeader />
        ) : (
          <DrawerHeader
            startAdornment={(<DrawerHeaderTitle />)}
            endAdornment={(
              <IconButton
                aria-label="drawer-close"
                onClick={handleDrawerClose}
                icon={Close}
              />
            )}
          />
        )}
      </div>

      <DrawerBody
        data-testid="header-drawer-content"
        data-component="HeaderDrawerContent"
        className="sui-flex-auto sui-overflow-y-auto sui-bg-primary"
      >
        <ErrorBoundary id="header-drawer-content" name="header-drawer-content">
          <Suspense fallback={<div />}>
            { drawerContent }
          </Suspense>
        </ErrorBoundary>
      </DrawerBody>
    </AnchoredDrawer>
  );
};

HeaderDrawer.displayName = 'HeaderDrawer';

export default memo(HeaderDrawer);
